// src/sectors/pilates/payments/receive/SessionPickerStep.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Icon from '@mui/material/Icon';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import { sessionsApi } from 'sectors/pilates/sessions/api/sessionsApi';

// ---------- helpers ----------
function hm(min) {
  const m = Number(min || 0);
  const h = String(Math.floor(m / 60)).padStart(2, '0');
  const mm = String(m % 60).padStart(2, '0');
  return `${h}:${mm}`;
}
function todayLocalDateStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}
function trMoney(n) {
  const x = Number(n || 0);
  return `₺${x.toLocaleString('tr-TR')}`;
}
function numOr0(v) {
  const x = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(x) ? x : 0;
}

function normalizeRow(x) {
  if (!x) return null;
  const debt = x.sessionDebtTry ?? x.debtTry ?? 0;
  return {
    sessionId: x.sessionId ?? null,
    dayISO: x.dayISO ?? null,
    startMin: x.startMin ?? null,
    durationMin: x.durationMin ?? null,
    activityName: x.activityName ?? null,
    trainerId: x.trainerId ?? null,
    trainerName: x.trainerName ?? null,

    sessionParticipantId: x.sessionParticipantId ?? x.id ?? null,
    memberId: x.memberId ?? null,
    fullName: x.fullName ?? null,
    phone: x.phone ?? null,

    totalTry: x.totalTry ?? null,
    paidTry: x.paidTry ?? 0,

    sessionDebtTry: Number(debt || 0),
    paymentStatus: x.paymentStatus ?? null,
  };
}

function MethodChips({ method, setMethod }) {
  const items = [
    ['CASH', 'Nakit', 'payments'],
    ['CARD', 'Kart', 'credit_card'],
    ['TRANSFER', 'Havale', 'swap_horiz'],
  ];
  return (
    <MDBox display="flex" gap={1} flexWrap="wrap">
      {items.map(([key, label, icon]) => {
        const active = method === key;
        return (
          <Chip
            key={key}
            icon={<Icon sx={{ fontSize: 18 }}>{icon}</Icon>}
            label={label}
            onClick={() => setMethod(key)}
            sx={{
              height: 34,
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 800,
              background: active ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.03)',
            }}
          />
        );
      })}
    </MDBox>
  );
}

export function SessionDebtPayDialog({ open, onClose, row, onPaid }) {
  const theme = useMuiTheme();
  const [method, setMethod] = useState('CASH');
  const [amountTry, setAmountTry] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    const debt = Number(row?.sessionDebtTry || 0);
    setMethod('CASH');
    setAmountTry(debt > 0 ? String(debt) : '');
    setPaidDate(todayLocalDateStr()); // ✅ eklendi
    setNote(
      row
        ? `Seans tahsilatı (${row.dayISO} ${hm(row.startMin)} • ${row.activityName || 'Seans'})`
        : ''
    );
    setSaving(false);
    setErr('');
  }, [open, row]);

  const debt = Number(row?.sessionDebtTry || 0);
  const amount = numOr0(amountTry);

  const canPay = !!row?.sessionParticipantId && debt > 0 && amount > 0 && amount <= debt && !saving;

  const submit = async () => {
    if (!canPay) return;
    try {
      setSaving(true);
      setErr('');

      await sessionsApi.receiveSessionPayment({
        sessionParticipantId: row.sessionParticipantId,
        amountTry: Math.round(amount),
        method,
        note: (note || '').trim() || undefined,
        paidDate: paidDate || todayLocalDateStr(),
      });

      onPaid?.(); // liste yenile, toast vs
      onClose?.();
    } catch (e) {
      console.log('❌ receiveSessionPayment error', e);
      setErr(e?.response?.data?.message || e?.message || 'Ödeme alınamadı');
    } finally {
      setSaving(false);
    }
  };
  const [paidDate, setPaidDate] = useState(todayLocalDateStr());
  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1.2 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDBox
              sx={{
                width: 36,
                height: 36,
                borderRadius: '12px',
                display: 'grid',
                placeItems: 'center',
                background: alpha(theme.palette.warning.main, 0.14),
              }}
            >
              <Icon sx={{ color: theme.palette.warning.main }}>payments</Icon>
            </MDBox>
            <MDBox>
              <MDTypography fontWeight="bold">Seans Borcu Tahsilatı</MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                #{row?.sessionParticipantId} • {row?.dayISO} {hm(row?.startMin)} •{' '}
                {row?.activityName || 'Seans'}
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDButton
            variant="text"
            color="dark"
            onClick={onClose}
            disabled={saving}
            sx={{ borderRadius: '12px' }}
          >
            Kapat
          </MDButton>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, display: 'grid', gap: 1.2 }}>
        <MDBox
          sx={{
            p: 1.1,
            borderRadius: '14px',
            background: alpha(theme.palette.warning.main, 0.07),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.16)}`,
          }}
        >
          <MDTypography variant="caption" sx={{ opacity: 0.8 }}>
            Kalan Borç
          </MDTypography>
          <MDTypography fontWeight="bold">{trMoney(debt)}</MDTypography>
        </MDBox>

        <MethodChips method={method} setMethod={setMethod} />

        <MDInput
          label="Alınan Ücret (₺)"
          value={amountTry}
          onChange={(e) => setAmountTry(e.target.value)}
          type="number"
          fullWidth
          inputProps={{ min: 0, max: debt }}
        />

        <MDInput
          label="Açıklama (opsiyonel)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
        />

        {!!err ? (
          <MDTypography variant="caption" sx={{ color: 'error.main' }}>
            {err}
          </MDTypography>
        ) : null}

        {amount > debt ? (
          <MDTypography variant="caption" sx={{ color: 'error.main' }}>
            Tutar borçtan büyük olamaz.
          </MDTypography>
        ) : null}
        <MDInput
          label="Ödeme Tarihi"
          value={paidDate}
          onChange={(e) => setPaidDate(e.target.value)}
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <MDButton
          variant="outlined"
          color="dark"
          onClick={onClose}
          disabled={saving}
          sx={{ borderRadius: '12px' }}
        >
          Vazgeç
        </MDButton>
        <MDButton
          variant="contained"
          color="warning"
          onClick={submit}
          disabled={!canPay}
          sx={{ borderRadius: '12px' }}
        >
          {saving ? 'Ödeme alınıyor...' : 'Ödemeyi Al'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export function SessionPaymentTab({ limit = 200 }) {
  const theme = useMuiTheme();

  const [q, setQ] = useState('');
  const [method, setMethod] = useState('CASH'); // list ekranında da gösteriyoruz
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);

  const [onlyDebt, setOnlyDebt] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [payOpen, setPayOpen] = useState(false);

  const tRef = useRef(null);

  const load = async (queryStr) => {
    try {
      setLoading(true);
      setErr('');

      const res = await sessionsApi.pendingSessionPayments({
        q: queryStr || undefined,
        limit,
      });

      const arr = Array.isArray(res) ? res : res?.items ?? res?.content ?? [];
      const normalized = (Array.isArray(arr) ? arr : []).map(normalizeRow).filter(Boolean);

      setRows(normalized);

      setSelected((prev) => {
        if (!prev?.sessionParticipantId) return prev;
        const ok = normalized.some((r) => r.sessionParticipantId === prev.sessionParticipantId);
        return ok ? prev : null;
      });
    } catch (e) {
      console.log('❌ pendingSessionPayments error', e);
      setErr(e?.response?.data?.message || e?.message || 'Seans borçları alınamadı');
      setRows([]);
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => load(q), 250);
    return () => tRef.current && clearTimeout(tRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const filtered = useMemo(() => {
    const base = Array.isArray(rows) ? rows : [];
    const list = onlyDebt ? base.filter((r) => Number(r.sessionDebtTry || 0) > 0) : base;

    // küçük UX: borcu büyük olan üstte
    return list.sort((a, b) => Number(b.sessionDebtTry || 0) - Number(a.sessionDebtTry || 0));
  }, [rows, onlyDebt]);

  const canOpenPay = !!selected && Number(selected.sessionDebtTry || 0) > 0;

  return (
    <>
      <MDBox sx={{ display: 'grid', gap: 1 }}>
        <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <MDInput
            label="Ara (isim / tel / katılımcıId)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            fullWidth
          />

          <Chip
            label={onlyDebt ? 'Sadece borçlu' : 'Hepsi'}
            onClick={() => setOnlyDebt((x) => !x)}
            sx={{
              height: 34,
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 700,
              background: onlyDebt
                ? alpha(theme.palette.warning.main, 0.12)
                : alpha(theme.palette.grey[500], 0.12),
            }}
          />
        </MDBox>

        {/* opsiyonel: list ekranında method seç */}
        <MethodChips method={method} setMethod={setMethod} />

        <MDBox sx={{ maxHeight: 340, overflow: 'auto', pr: 0.5 }}>
          {loading ? (
            <MDBox display="flex" alignItems="center" justifyContent="center" sx={{ py: 3 }}>
              <CircularProgress size={22} />
              <MDTypography variant="caption" sx={{ ml: 1, opacity: 0.75 }}>
                Yükleniyor…
              </MDTypography>
            </MDBox>
          ) : err ? (
            <MDBox
              sx={{
                p: 1.2,
                borderRadius: '14px',
                background: alpha(theme.palette.error.main, 0.08),
              }}
            >
              <MDTypography variant="caption" color="error">
                {err}
              </MDTypography>
              <MDButton
                variant="text"
                color="error"
                onClick={() => load(q)}
                sx={{ mt: 0.5, borderRadius: '12px' }}
              >
                Tekrar dene
              </MDButton>
            </MDBox>
          ) : (
            <>
              {filtered.map((r) => {
                const active = selected?.sessionParticipantId === r.sessionParticipantId;
                const debt = Number(r.sessionDebtTry || 0);

                return (
                  <MDBox
                    key={r.sessionParticipantId}
                    onClick={() => setSelected({ ...r, _method: method })}
                    sx={{
                      p: 1.1,
                      borderRadius: '16px',
                      border: `1px solid ${alpha(
                        theme.palette.warning.main,
                        active ? 0.35 : 0.18
                      )}`,
                      background: active
                        ? alpha(theme.palette.warning.main, 0.09)
                        : alpha(theme.palette.grey[500], 0.05),
                      cursor: 'pointer',
                      mb: 0.8,
                    }}
                  >
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <MDBox sx={{ minWidth: 0 }}>
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ display: 'block' }}
                          >
                            {r.fullName || '—'}
                          </MDTypography>

                          {r.memberId ? (
                            <Chip
                              size="small"
                              label="Üye"
                              sx={{ height: 22, borderRadius: '10px', fontWeight: 800 }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Misafir"
                              sx={{
                                height: 22,
                                borderRadius: '10px',
                                fontWeight: 800,
                                background: alpha(theme.palette.info.main, 0.12),
                              }}
                            />
                          )}
                        </MDBox>

                        <MDTypography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>
                          #{r.sessionParticipantId} • {r.dayISO} {hm(r.startMin)} •{' '}
                          {r.activityName || 'Seans'}
                        </MDTypography>

                        <MDTypography
                          variant="caption"
                          sx={{ opacity: 0.85, display: 'block', mt: 0.2 }}
                        >
                          Borç: {trMoney(debt)} (Toplam {trMoney(r.totalTry)} • Ödenen{' '}
                          {trMoney(r.paidTry)})
                        </MDTypography>

                        {!!r.phone ? (
                          <MDTypography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                            Tel: {r.phone}
                          </MDTypography>
                        ) : null}
                      </MDBox>

                      <Icon sx={{ opacity: active ? 1 : 0.35 }}>check_circle</Icon>
                    </MDBox>
                  </MDBox>
                );
              })}

              {!filtered.length ? (
                <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                  Ödeme bekleyen seans bulunamadı.
                </MDTypography>
              ) : null}
            </>
          )}
        </MDBox>

        <Divider />

        <MDButton
          variant="gradient"
          color="warning"
          disabled={!canOpenPay}
          fullWidth
          onClick={() => setPayOpen(true)}
          sx={{ borderRadius: '16px', textTransform: 'none', fontWeight: 900, height: 44 }}
        >
          Seçili Seansın Borcunu Hazırla
        </MDButton>
      </MDBox>

      {/* ✅ BORÇ ÖDEME EKRANI */}
      <SessionDebtPayDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        row={{
          ...(selected || {}),
          // list ekranında seçilen methodu modal default yapsın istersen:
          // methodu row içine koyduk ama dialog içinde tekrar set ediyoruz (istersen orayı da bağlarız)
        }}
        onPaid={() => load(q)}
      />
    </>
  );
}
